import { useState } from "react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Loader2, Sparkles, Target, ArrowRight, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const SERVICE_BLURBS = {
  "AI & Operations Assessment":
    "Start with clarity. Map the business, find friction, and prioritize the highest-impact AI and operations opportunities.",
  "Lead Funnels & AI Automation":
    "Improve lead capture, speed to lead, follow-up, CRM flow, and automated customer communication.",
  "30–90 Day Consulting Sprint":
    "Move from insight to implementation with focused workstreams, owners, metrics, and weekly execution.",
};

const URGENCY_STYLE = {
  High: "bg-red-100 text-red-700 border-red-200",
  Medium: "bg-gold/20 text-[#8A6F2A] border-gold/40",
  Low: "bg-navy/10 text-navy/70 border-navy/20",
};

export default function ServiceRecommendationCard({ interviewId, data, setData }) {
  const [generating, setGenerating] = useState(false);
  const rec = data?.service_recommendation;

  const generate = async () => {
    setGenerating(true);
    try {
      const r = await api.post(`/interviews/${interviewId}/service-recommendation`);
      setData((prev) => ({
        ...prev,
        service_recommendation: r.data,
        recommended_service_path: r.data.primary_service || prev?.recommended_service_path,
      }));
      toast.success("Goldline path recommended.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not generate recommendation. Add more intake data first.");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="bg-white border border-navy/10 p-8 md:p-10" data-testid="service-recommendation-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="eyebrow text-gold">Recommended Goldline Path</div>
          <h2 className="font-display text-3xl text-navy mt-2">
            {rec?.primary_service || "Pick the right service path"}
          </h2>
          <div className="w-12 h-[2px] bg-gold mt-3" />
        </div>
        <Button
          onClick={generate}
          disabled={generating}
          className="bg-navy hover:bg-navy/90 text-ivory rounded-none"
          data-testid="generate-service-rec-btn"
        >
          {generating ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Routing…</>
          ) : (
            <><Sparkles className="w-4 h-4 mr-2" /> {rec ? "Refresh recommendation" : "Recommend Goldline path"}</>
          )}
        </Button>
      </div>

      {!rec && (
        <p className="mt-4 text-sm text-navy/60 max-w-2xl">
          AI gives you the first draft. Goldline makes the call. Fill in basics, drop in a transcript,
          or generate an assessment first — then click <em>Recommend Goldline path</em> to route this client
          to the right Goldline service.
        </p>
      )}

      {rec && (
        <div className="mt-6 grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-5">
            <div className="p-5 bg-ivory border-l-4 border-gold">
              <p className="text-sm text-navy/60 leading-relaxed">
                {SERVICE_BLURBS[rec.primary_service] || rec.reasoning}
              </p>
            </div>

            {rec.reasoning && (
              <div>
                <div className="text-[10px] uppercase tracking-widest text-navy/40 font-semibold mb-1">Reasoning</div>
                <p className="text-sm text-navy/80">{rec.reasoning}</p>
              </div>
            )}

            {!!(rec.evidence || []).length && (
              <div>
                <div className="text-[10px] uppercase tracking-widest text-navy/40 font-semibold mb-2">Evidence</div>
                <ul className="space-y-1.5">
                  {rec.evidence.map((ev, i) => (
                    <li key={i} className="flex gap-2 text-sm text-navy/75">
                      <CheckCircle2 className="w-4 h-4 text-gold flex-shrink-0 mt-0.5" /> {ev}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {!!(rec.suggested_next_call_agenda || []).length && (
              <div>
                <div className="text-[10px] uppercase tracking-widest text-navy/40 font-semibold mb-2">Suggested next-call agenda</div>
                <ol className="list-decimal list-inside space-y-1 text-sm text-navy/75">
                  {rec.suggested_next_call_agenda.map((a, i) => <li key={i}>{a}</li>)}
                </ol>
              </div>
            )}

            {rec.suggested_first_step && (
              <div className="bg-navy text-ivory p-5 flex items-start gap-3">
                <Target className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                <div>
                  <div className="eyebrow text-gold mb-1">Suggested first step</div>
                  <p className="text-sm leading-relaxed">{rec.suggested_first_step}</p>
                </div>
              </div>
            )}
          </div>

          <div className="space-y-3 text-xs">
            <div className="p-4 bg-ivory border border-navy/10">
              <div className="uppercase tracking-widest text-navy/40 font-semibold mb-1">Urgency</div>
              <span className={`inline-block px-2 py-1 border font-semibold uppercase tracking-wider ${URGENCY_STYLE[rec.urgency] || URGENCY_STYLE.Medium}`}>
                {rec.urgency || "—"}
              </span>
            </div>
            <div className="p-4 bg-ivory border border-navy/10">
              <div className="uppercase tracking-widest text-navy/40 font-semibold mb-1">Confidence</div>
              <div className="text-sm text-navy font-semibold">{rec.confidence || "—"}</div>
            </div>
            {rec.secondary_service && (
              <div className="p-4 bg-ivory border border-navy/10">
                <div className="uppercase tracking-widest text-navy/40 font-semibold mb-1">Secondary path</div>
                <div className="text-sm text-navy">{rec.secondary_service}</div>
              </div>
            )}
            {rec.recommended_sequence && (
              <div className="p-4 bg-gold/15 border border-gold">
                <div className="uppercase tracking-widest text-[#8A6F2A] font-semibold mb-1">Recommended sequence</div>
                <div className="text-sm text-navy font-semibold flex items-center gap-1">
                  <ArrowRight className="w-3.5 h-3.5 text-gold" /> {rec.recommended_sequence}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
